import { type AxiosError } from "axios";
import { useEffect, useState } from "react";

import { type ExceptionResponse } from "types/apiResponse/apiResponse";

import { showApixErrorMessage } from "utilities/api/apix-handler";

import { type SpecificationMappingData, type SpecificationResponse } from "handlers/models";
import {
    specificationsDefaultsGetV2,
    useSpecificationsDefaultsPostV2,
} from "handlers/specifications/specifications";

interface IUseSpecificationCreateDefaultsOptions {
    enabled: boolean;
    jobId: number;
    specificationMapping?: SpecificationMappingData;
}

/**
 * GET defaults; POST instead when mapping is supplied (estimate-group flow). Returns the raw
 * response so the viewer's `initialValues` memo handles the EditorContent transform on the
 * same path as the by-id fetch.
 */
export function useSpecificationCreateDefaults({
    enabled,
    jobId,
    specificationMapping,
}: IUseSpecificationCreateDefaultsOptions) {
    const [defaults, setDefaults] = useState<SpecificationResponse | undefined>(undefined);
    const [isLoading, setIsLoading] = useState(enabled);
    const { mutateAsync: postDefaults } = useSpecificationsDefaultsPostV2();

    useEffect(() => {
        if (!enabled) {
            setDefaults(undefined);
            setIsLoading(false);
            return;
        }
        const controller = new AbortController();
        setIsLoading(true);
        const fetchDefaults = async () => {
            try {
                const response = specificationMapping
                    ? await postDefaults({ data: specificationMapping, params: { jobId } })
                    : await specificationsDefaultsGetV2({ jobId }, undefined, controller.signal);
                if (controller.signal.aborted) return;
                setDefaults(response.data);
            } catch (error) {
                if (controller.signal.aborted) return;
                showApixErrorMessage(error as AxiosError<ExceptionResponse>);
            } finally {
                if (!controller.signal.aborted) setIsLoading(false);
            }
        };
        void fetchDefaults();
        return () => {
            controller.abort();
        };
    }, [enabled, jobId, specificationMapping, postDefaults]);

    return { defaults, isLoading };
}
